import forge from 'apis/forge';
import DsPicker from 'components/Widget/DsPicker';
import { DesignSystem } from 'dragonmouse-shared/interfaces';
import { shallowEqual, useSelector } from 'react-redux';
import {
	selectActiveScene,
	selectActiveSceneDesignSystem,
	selectDesignSystems,
	selectActiveSceneLinkedDesignSystemVersion,
	selectActiveSceneLatestDesignSystemVersion,
} from 'redux/db/reducers';
import { canEdit } from 'redux/db/util';
import { RootReducer } from 'redux/reducers';
import { displayViewOnlyModal } from 'redux/mode/util';
import { useNavigate } from 'react-router';
import { toast } from 'react-toastify';
import * as routes from 'routes';

interface DsPickerWrapperProps {
	compactMode?: 'full' | 'compact';
}

const DsPickerWrapper = ({ compactMode = 'full' }: DsPickerWrapperProps) => {
	const navigate = useNavigate();

	const { scene, designSystems, activeDesignSystem, linkedVersion, latestVersion } = useSelector(
		(r: RootReducer) => {
			return {
				scene: selectActiveScene(r.db, r.mode.activeSceneId),
				designSystems: selectDesignSystems(r.db),
				activeDesignSystem: selectActiveSceneDesignSystem(r.db, r.mode.activeSceneId),
				linkedVersion: selectActiveSceneLinkedDesignSystemVersion(r.db),
				latestVersion: selectActiveSceneLatestDesignSystemVersion(r.db),
			};
		},
		shallowEqual
	);

	// Calculate status based on versions
	const dsStatus: 'latest' | 'publishing' | 'updateAvailable' | null = (() => {
		if (!linkedVersion || !latestVersion) return null;
		if (linkedVersion.versionNumber === latestVersion.versionNumber) {
			// Check if latest is still publishing
			const promptStatus = latestVersion.manifest?.promptGenerationStatus;
			if (promptStatus === 'pending' || promptStatus === 'generating') {
				return 'publishing';
			}
			return 'latest';
		}
		// Check if latest is ready for use
		const promptStatus = latestVersion.manifest?.promptGenerationStatus;
		if (promptStatus === 'complete') {
			return 'updateAvailable';
		}
		if (promptStatus === 'pending' || promptStatus === 'generating') {
			return 'publishing';
		}
		return null;
	})();

	const onSelect = async (designSystem: DesignSystem | null) => {
		if (!scene || !canEdit(scene)) {
			displayViewOnlyModal();
			return;
		}

		// Unlinking is always allowed
		if (!designSystem) {
			try {
				await forge.linkSceneDesignSystem({
					sceneId: scene.id,
					designSystemId: null,
				});
			} catch (error) {
				console.error('Failed to unlink design system:', error);
				toast.error('Failed to unlink design system. Please try again.');
			}
			return;
		}

		// Validate design system before linking
		try {
			const versionInfo = await forge.getDesignSystemVersionInfo(designSystem.id);

			// Check if DS is legacy format
			if (versionInfo.isLegacy) {
				toast.warning(
					'This design system uses an older format and must be converted before it can be linked to projects.'
				);
				return;
			}

			// Check if DS has a published version
			if (!versionInfo.latestPublishedVersionNumber) {
				toast.warning(
					'This design system has no published version yet. If you just created it, please wait a couple of minutes and try again.'
				);
				return;
			}

			// Check if the version is ready for use (prompt generation complete)
			const promptStatus = versionInfo.version?.manifest?.promptGenerationStatus;
			if (promptStatus !== 'complete') {
				if (promptStatus === 'pending' || promptStatus === 'generating') {
					toast.warning(
						'This design system is not ready for use yet. Please wait a couple of minutes and try again.'
					);
				} else if (promptStatus === 'failed') {
					toast.warning('This design system failed to publish. Please try publishing again.');
				} else {
					toast.warning(
						'This design system is not ready for use yet. Please wait a couple of minutes and try again.'
					);
				}
				return;
			}

			// DS is valid - proceed with linking
			await forge.linkSceneDesignSystem({
				sceneId: scene.id,
				designSystemId: designSystem.id,
			});
		} catch (error) {
			console.error('Failed to validate or link design system:', error);
			toast.error('Failed to link design system. Please try again.');
		}
	};

	const onCreateNew = () => {
		if (!canEdit(scene)) {
			displayViewOnlyModal();
			return;
		}
		navigate(routes.createDesignSystem(scene?.id));
	};

	const onUpgrade = async () => {
		if (!scene || !activeDesignSystem) return;
		if (!canEdit(scene)) {
			displayViewOnlyModal();
			return;
		}

		// Re-link the same design system, which will update to the latest published version
		try {
			await forge.linkSceneDesignSystem({
				sceneId: scene.id,
				designSystemId: activeDesignSystem.id,
			});
		} catch (error) {
			console.error('Failed to upgrade design system:', error);
			toast.error('Failed to upgrade design system. Please try again.');
		}
	};

	const selection = activeDesignSystem
		? {
				id: activeDesignSystem.id,
				status: dsStatus,
				linkedVersion,
				latestVersion,
		  }
		: null;

	return (
		<DsPicker
			selection={selection}
			designSystems={designSystems}
			compactMode={compactMode}
			onSelect={onSelect}
			onCreateNew={onCreateNew}
			onUpgrade={onUpgrade}
		/>
	);
};

export default DsPickerWrapper;
